import React, { useEffect, useRef, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import PageLayout from '../../components/layout/PageLayout';
import {
  AccountBoundary,
  SettingsHeader,
  useSettings,
  useRequestScope,
  unwrap,
} from '../settings/shared';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { translate } from './model';
import './Notifications.css';
export const preferenceKeys = [
  'activityEnabled',
  'followingEnabled',
  'releasesEnabled',
  'archiveEnabled',
  'announcementsEnabled',
  'marketingEnabled',
];
export function normalizePush(value) {
  if (
    !value ||
    typeof value.enabled !== 'boolean' ||
    !Number.isFinite(value.registeredDevices) ||
    value.registeredDevices < 0 ||
    preferenceKeys.slice(0, 5).some((k) => typeof value[k] !== 'boolean') ||
    (value.marketingEnabled != null && typeof value.marketingEnabled !== 'boolean')
  )
    throw Error('Invalid push settings');
  return { ...value, marketingEnabled: !!value.marketingEnabled };
}
export default function PushPreferences() {
  return <AccountBoundary component={Preferences} />;
}
function Preferences({ user }) {
  const { locale } = useWebLocale(),
    { style } = useSettings(),
    request = useRequestScope(user),
    t = (k, v) => translate(locale, k, v),
    en = locale === 'en';
  const [data, setData] = useState(null),
    [loading, setLoading] = useState(!!user),
    [error, setError] = useState(''),
    [loadFailed, setLoadFailed] = useState(false),
    [busy, setBusy] = useState(false),
    [attempt, setAttempt] = useState(0);
  const lock = useRef(false);
  const dataToken = useRef(null);
  const rows = useRef(null);
  const lastCategory = useRef(null);
  const restoreAfterRetry = useRef(false);
  useEffect(() => {
    if (!restoreAfterRetry.current || loading || loadFailed || !data) return;
    restoreAfterRetry.current = false;
    const switches = Array.from(rows.current?.querySelectorAll('[role="switch"]') || []);
    const target =
      switches.find((node) => node.dataset.preference === lastCategory.current) || switches[0];
    target?.focus({ preventScroll: true });
  }, [loading, loadFailed, data]);
  useEffect(() => {
    if (!user) return;
    const r = request();
    setLoading(true);
    setLoadFailed(false);
    setError('');
    api
      .getAuth('/notifications/push/settings', r.options)
      .then((res) => {
        if (r.current()) {
          const next = normalizePush(unwrap(res));
          dataToken.current = localStorage.getItem('POSTPOP_AUTH_TOKEN');
          setData(next);
        }
      })
      .catch(() => {
        if (r.current()) {
          setError(t('push.loadError'));
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (r.mounted()) {
          setLoading(false);
          if (!r.current()) {
            setLoadFailed(true);
            setError(t('common.retryLater'));
          }
        }
        r.finish();
      });
    return r.cancel;
    // AccountBoundary resets on every account session change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);
  const update = async (key, value) => {
    if (lock.current || !data || loading || loadFailed || !user) return;
    lastCategory.current = key;
    // Values loaded under a prior credential cannot authorize a new category change.
    if (dataToken.current !== localStorage.getItem('POSTPOP_AUTH_TOKEN')) {
      setLoadFailed(true);
      setError(t('common.retryLater'));
      return;
    }
    if (value && (!data.enabled || data.registeredDevices === 0)) {
      setError(
        en
          ? 'Enable notifications in the Postpop app first. Device permission cannot be granted here.'
          : '먼저 포스트팝 앱에서 알림을 켜 주세요. 기기 권한은 이 화면에서 허용할 수 없습니다.',
      );
      return;
    }
    lock.current = true;
    setBusy(true);
    setError('');
    const r = request();
    try {
      const res = await api.putAuth('/notifications/push/settings', { [key]: value }, r.options);
      if (r.current()) setData(normalizePush(unwrap(res)));
    } catch {
      if (r.current()) setError(t('push.saveError'));
    } finally {
      lock.current = false;
      if (r.mounted()) {
        setBusy(false);
        if (!r.current()) {
          setLoadFailed(true);
          setError(t('common.retryLater'));
        }
      }
      r.finish();
    }
  };
  return (
    <PageLayout paddingTop={0}>
      <div className="pp-settings pp-push-preferences" style={style}>
        <SettingsHeader title={t('push.title')} busy={busy} />
        <section className="pp-push-summary">
          <span className="pp-push-icon">
            <FiBell size={20} />
          </span>
          <div>
            <strong>{t('push.notifications')}</strong>
            <p>{t(user ? 'push.userDescription' : 'push.guestDescription')}</p>
            <small>
              {en
                ? 'Device permissions and overall delivery are configured in the app.'
                : '기기 권한과 전체 알림 수신은 앱에서 설정합니다.'}
            </small>
          </div>
        </section>
        <p className="pp-push-browser-note">
          {en
            ? 'These are your app account’s notification preferences, not browser delivery settings. Category changes also apply to the app. Browser push is not available.'
            : '앱 계정의 알림 종류를 설정하는 화면입니다. 변경하면 앱에도 적용되며, 브라우저 푸시 수신을 켜는 기능은 아닙니다.'}
        </p>
        {!user ? (
          <div className="pp-push-guest">
            <p>
              {en
                ? 'Guest notification settings belong to each app installation. Open Postpop on your device to change them.'
                : '비회원 알림 설정은 설치된 앱 기기에 저장됩니다. 해당 기기의 포스트팝 앱에서 변경해 주세요.'}
            </p>
            <Link to="/login?redirectUrl=%2Fmypage%2Fpush-settings">{t('common.login')}</Link>
          </div>
        ) : (
          <>
            {loading && <p role="status">{t('common.loading')}</p>}
            {data && (
              <div className="pp-push-rows" ref={rows}>
                {preferenceKeys.map((key) => {
                  const name = key.replace('Enabled', '');
                  return (
                    <div className="pp-push-row" key={key}>
                      <div>
                        <strong>{t('push.' + name)}</strong>
                        <p>{t('push.' + name + 'Description')}</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        data-preference={key}
                        className="pp-push-switch"
                        aria-checked={data[key]}
                        aria-label={t('push.' + name)}
                        disabled={busy || loading || loadFailed}
                        onClick={() => update(key, !data[key])}
                      >
                        <span />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {error && (
              <div role="alert">
                <p>{error}</p>
                <button
                  disabled={busy || loading}
                  onClick={() => {
                    restoreAfterRetry.current = true;
                    setAttempt((n) => n + 1);
                  }}
                >
                  {t('common.retry')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
}
